/* eslint-disable */
import { getEnvs } from './envs'
const { NODE_ENV } = getEnvs()

const isProductionBuild = NODE_ENV === 'production'

/**
 * Core logging implementation that formats console output with styled backgrounds
 * and colors. Only outputs in development builds.
 *
 * @param backgroundColor - CSS background color for the log label.
 * @param fontColor      - CSS text color for the log label.
 * @param text           - Primary message to log (string or object).
 * @param args           - Additional arguments to display in a collapsible group.
 */
const write = (
  backgroundColor: string,
  fontColor: string,
  text: unknown,
  ...args: unknown[]
): void => {
  if (isProductionBuild) {
    return
  }

  const consoleArgs: string[] = [
    ``,
    `background: ${'#c8c8ff'};`,
    `background: ${'#9696ff'};`,
    `color: ${fontColor}; background: ${backgroundColor};`,
    `background: ${'#9696ff'};`,
    `background: ${'#c8c8ff'};`
  ]

  const isString: boolean = typeof text === 'string'

  !isString && args.unshift(text)

  consoleArgs[0] = `%c %c %c ${isString ? text : 'log'} %c %c`

  if (args.length) {
    console.groupCollapsed(...consoleArgs)

    for (const arg of args) {
      console.log(arg)
    }

    console.groupEnd()
  } else {
    console.log(...consoleArgs)
  }

  return
}

/**
 * Enhanced logging implementation that includes a stack trace. Only outputs
 * in **production** builds (note the inverted condition vs `write`).
 *
 * @param backgroundColor - CSS background color for the log label.
 * @param fontColor      - CSS text color for the log label.
 * @param text           - Primary message to log (string or object).
 * @param args           - Additional arguments to display in a collapsible group.
 */
const writeTrace = (
  backgroundColor: string,
  fontColor: string,
  text: unknown,
  ...args: unknown[]
): void => {
  if (isProductionBuild) {
    return
  }

  const consoleArgs: string[] = [
    ``,
    `background: #c8c8ff;`,
    `background: #9696ff;`,
    `color: ${fontColor}; background: ${backgroundColor};`,
    `background: #9696ff;`,
    `background: #c8c8ff;`
  ]

  const isString: boolean = typeof text === 'string'

  !isString && args.unshift(text)

  consoleArgs[0] = `%c %c %c ${isString ? text : 'log'} %c %c`

  console.groupCollapsed(...consoleArgs)

  args.forEach(arg => {
    console.log(arg)
  })

  console.trace()
  console.groupEnd()

  return
}

/**
 * Logs socketdebug information with purple styling.
 * Only visible in production builds.
 *
 * @param args - Debug message and optional additional data to log.
 */
export const logSocketDebug = (...args: unknown[]): void =>
  write('#800080', '#ffffff', args.shift(), ...args)

/**
 * Logs debug information with blue styling and includes a stack trace.
 * Only visible in production builds.
 *
 * @param args - Debug message and optional additional data to log.
 */
export const logDebug = (...args: unknown[]): void =>
  writeTrace('#0000ff', '#ffffff', args.shift(), ...args)

/**
 * Logs general information with gray styling. Only visible in development builds.
 *
 * @param args - Info message and optional additional data to log.
 */
export const logInfo = (...args: unknown[]): void =>
  write('#9e9e9e', '#ffffff', args.shift(), ...args)

/**
 * Logs error information with red styling and includes a stack trace.
 * Only visible in production builds.
 *
 * @param args - Error message and optional additional data to log.
 */
export const logError = (...args: unknown[]): void =>
  writeTrace('#ff0000', '#ffffff', args.shift(), ...args)
/**
 * Logs rejection/failure information with dark red styling.
 * Only visible in development builds.
 *
 * @param args - Rejection message and optional additional data to log.
 */
export const logReject = (...args: unknown[]): void =>
  write('#8B0000', '#ffffff', args.shift(), ...args)

/**
 * Logs success information with green styling. Only visible in development builds.
 *
 * @param args - Success message and optional additional data to log.
 */
export const logSuccess = (...args: unknown[]): void =>
  write('#308751', '#ffffff', args.shift(), ...args)

/**
 * Logs warning information with orange styling. Only visible in development builds.
 *
 * @param args - Warning message and optional additional data to log.
 */
export const logWarning = (...args: unknown[]): void =>
  write('#FFA500', '#000000', args.shift(), ...args)
